<template>

    <!-- Loader -->
    <app-loader :loading="loading" />

    <div class="py-2 px-4 sm:px-8">

        <h1 class="flex pt-3 pb-8 items-center border-b border-gray-700 mb-8 sm:pt-6">
            <div class="text-2xl font-bold text-gray-200 sm:text-3xl">{{ $t('setup.title') }}</div>
            <span v-if="isGod"
              class="ml-auto text-1xl font-bold text-red-500 px-4 py-2
                    inline-flex justify-center
                    shadow-sm ring-1 ring-inset ring-gray-900
                    rounded-md bg-gray-900/70"
            >
              {{ $t('setup.god') }}
            </span>
        </h1>

        <!-- Regular users only see their own account -->
        <!-- Everybody's own data, the GOD user's too: it is where the language of the account is changed -->
        <user-info-card :user="currentUser" @edit="users.openEdit($event)" />

        <setup-section
            :title="$t('setup.recurringBills')"
            :add-label="$t('setup.addRecurringBill')"
            :columns="billColumns"
            :items="bills.items"
            :row-class-condition="isInactive"
            @add="bills.openAdd()"
        >
            <template #header-actions>
                <button type="button" class="gray sm:w-fit w-full sm:ml-auto" @click="autoRecurringBill">
                    {{ $t('setup.automation') }}
                </button>
            </template>
            <template #actions="{ item }">
                <app-row-actions @edit="bills.openEdit(item)" @delete="bills.remove(item)" />
            </template>
        </setup-section>

        <setup-section
            :title="$t('setup.categories')"
            :add-label="$t('setup.addCategory')"
            :columns="categoryColumns"
            :items="categories.items"
            @add="categories.openAdd()"
        >
            <template #actions="{ item }">
                <app-row-actions @edit="categories.openEdit(item)" @delete="categories.remove(item)" />
            </template>
        </setup-section>

        <setup-section
            :title="$t('setup.subcategories')"
            :add-label="$t('setup.addSubcategory')"
            :columns="subcategoryColumns"
            :items="subcategories.items"
            @add="subcategories.openAdd()"
        >
            <template #actions="{ item }">
                <app-row-actions @edit="subcategories.openEdit(item)" @delete="subcategories.remove(item)" />
            </template>
        </setup-section>

        <!-- God level only -->
        <setup-section
            v-if="isGod"
            :title="$t('setup.users')"
            :add-label="$t('setup.addUser')"
            :columns="userColumns"
            :items="users.items"
            @add="users.openAdd()"
        >
            <template #actions="{ item }">
                <!-- The GOD user itself can't be edited or deleted here -->
                <app-row-actions
                    :show-edit="item.user_id > 1"
                    :show-delete="item.user_id > 1"
                    @edit="users.openEdit(item)"
                    @delete="users.remove(item)"
                />
            </template>
        </setup-section>

        <add-category-modal
            :is-add-modal-open="categories.isAddOpen"
            @update:is-add-modal-open="categories.isAddOpen = $event"
            @category-saved="refreshTables"
        />
        <edit-category-modal
            :is-edit-modal-open="categories.isEditOpen"
            :selected-category="categories.selected"
            @update:is-edit-modal-open="categories.isEditOpen = $event"
            @category-saved="refreshTables"
        />

        <add-subcategory-modal
            :is-add-modal-open="subcategories.isAddOpen"
            @update:is-add-modal-open="subcategories.isAddOpen = $event"
            @subcategory-saved="refreshTables"
        />
        <edit-subcategory-modal
            :is-edit-modal-open="subcategories.isEditOpen"
            :selected-subcategory="subcategories.selected"
            @update:is-edit-modal-open="subcategories.isEditOpen = $event"
            @subcategory-saved="refreshTables"
        />

        <add-bill-modal
            :is-add-modal-open="bills.isAddOpen"
            @update:is-add-modal-open="bills.isAddOpen = $event"
            @bill-saved="refreshTables"
        />
        <edit-bill-modal
            :is-edit-modal-open="bills.isEditOpen"
            :selected-bill="bills.selected"
            @update:is-edit-modal-open="bills.isEditOpen = $event"
            @bill-saved="refreshTables"
        />

        <add-user-modal
            :is-add-modal-open="users.isAddOpen"
            @update:is-add-modal-open="users.isAddOpen = $event"
            @user-saved="refreshTables"
        />
        <edit-user-modal
            :is-edit-modal-open="users.isEditOpen"
            :selected-user="users.selected"
            @update:is-edit-modal-open="users.isEditOpen = $event"
            @user-saved="refreshTables"
        />

    </div>

</template>

<script>
import { ref, computed, onMounted, inject } from 'vue';
import { useToast } from 'vue-toastification';
import apiClient from '@/api/apiClient';
import { useLoading } from '@/composables/useLoading';
import { t } from '@/i18n';
import { useCrudList } from '@/composables/useCrudList';
import { useOnLocaleChange } from '@/composables/useOnLocaleChange';
import AppLoader from "@/components/common/AppLoader.vue";
import AppRowActions from "@/components/common/AppRowActions.vue";
import SetupSection from "@/components/setup/SetupSection.vue";
import UserInfoCard from "@/components/setup/UserInfoCard.vue";
import AddCategoryModal from "@/components/setup/AddCategoryModal";
import EditCategoryModal from "@/components/setup/EditCategoryModal";
import AddSubcategoryModal from "@/components/setup/AddSubcategoryModal";
import EditSubcategoryModal from "@/components/setup/EditSubcategoryModal";
import AddBillModal from "@/components/setup/AddBillModal";
import EditBillModal from "@/components/setup/EditBillModal";
import AddUserModal from "@/components/setup/AddUserModal";
import EditUserModal from "@/components/setup/EditUserModal";

// Column titles are translated when they are read, so they follow the language
const categoryColumns = () => [
    { key: "category_name", label: t('fields.category') }
];
const subcategoryColumns = () => [
    { key: "category_name", label: t('fields.category') },
    { key: "subcategory_name", label: t('fields.subcategory') }
];
const billColumns = () => [
    { key: "name", label: t('fields.name') },
    { key: "subcategory_name", label: t('fields.subcategory') },
    { key: "day", label: t('fields.dayOfMonthColumn') },
    { key: "value", label: t('fields.value') },
    { key: "status", label: t('fields.status') }
];
const userColumns = () => [
    { key: "user_id", label: t('fields.id') },
    { key: "username", label: t('fields.name') },
    { key: "email", label: t('fields.email') }
];

export default {
    components: {
        AppLoader,
        AppRowActions,
        SetupSection,
        UserInfoCard,
        AddCategoryModal,
        EditCategoryModal,
        AddSubcategoryModal,
        EditSubcategoryModal,
        AddBillModal,
        EditBillModal,
        AddUserModal,
        EditUserModal
    },
    setup() {
        const toast = useToast();
        const { loading, track } = useLoading();

        // The GOD user (id 1) manages every account; everyone else only sees their own
        const userId = ref(localStorage.getItem('userId'));
        const isGod = computed(() => userId.value == 1);
        const currentUser = ref(null);

        const listOptions = { track, onDeleted: () => refreshTables() };
        const categories = useCrudList({
            entity: 'category',
            listUrl: '/get-categories.php',
            deleteUrl: '/delete-category.php',
            deleteKey: 'category',
            ...listOptions
        });
        const subcategories = useCrudList({
            entity: 'subcategory',
            listUrl: '/get-setup-subcategories.php',
            deleteUrl: '/delete-subcategory.php',
            deleteKey: 'subcategory',
            ...listOptions
        });
        const bills = useCrudList({
            entity: 'recurringBill',
            listUrl: '/get-recurring-bills.php',
            deleteUrl: '/delete-recurring-bills.php',
            deleteKey: 'bill',
            ...listOptions
        });
        const users = useCrudList({
            entity: 'user',
            listUrl: '/get-users.php',
            deleteUrl: '/delete-user.php',
            deleteKey: 'user',
            ...listOptions
        });

        // Inactive recurring bills are shown dimmed
        const isInactive = (item) => item.status === 0;

        const fetchCurrentUser = () => {
            return track(
                apiClient.get(`/get-user-details.php?userId=${userId.value}`)
                    .then((response) => {
                        currentUser.value = response.data;
                    })
                    .catch((error) => {
                        console.error('Error fetching user details:', error);
                    })
            );
        };

        // Generates this month's expenses from the active recurring bills
        const autoRecurringBill = async () => {
            try {
                const response = await apiClient.post('/auto-recurring-bills.php');
                toast.info(response.data.message);
            } catch (error) {
                console.error('Error triggering recurring bills generation:', error);
                toast.error(t('setup.automationError'));
            }
        };

        const refreshTables = () => {
            fetchCurrentUser();
            categories.fetchItems();
            subcategories.fetchItems();
            bills.fetchItems();

            // only the GOD user lists all users
            if (isGod.value) {
                users.fetchItems();
            }
        };

        onMounted(refreshTables);

        // names of categories and subcategories come from the API in the current language
        useOnLocaleChange(refreshTables);

        // Keep the user card in sync when the user is edited elsewhere (e.g. navbar)
        const eventBus = inject('eventBus');
        if (eventBus) {
            eventBus.$on('user-details-updated', (userData) => {
                currentUser.value = userData;
            });
        } else {
            console.error('eventBus is not injected properly.');
        }

        return {
            loading,
            isGod,
            currentUser,
            categories,
            subcategories,
            bills,
            users,
            categoryColumns: computed(categoryColumns),
            subcategoryColumns: computed(subcategoryColumns),
            billColumns: computed(billColumns),
            userColumns: computed(userColumns),
            isInactive,
            autoRecurringBill,
            refreshTables
        };
    }
}
</script>
